import {
  CmiPageConfigurationKeys,
  CmiPageConfigurations,
  cmiPageConfigurationsDefaultValues,
  CMI_PAGE_NAMESPACE,
} from '@epicuro-next/constants/rule-engine/cmi-page-rules';
import { selectConfigurationValueFrom } from '@epicuro-next/platform/configuration';
import { memoize } from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';

import { Articles } from '../models';
import { selectReplenishmentState } from '../reducers/replenishment.reducer';

export const selectReplenishments = createSelector(
  selectReplenishmentState,
  (state) => state.articles,
);

export const selectActiveReplenishmentMaterials = createSelector(
  selectReplenishments,
  (articles) =>
    articles &&
    Object.keys(articles).flatMap((key: string) => {
      const article = articles[key];

      const activeMaterialSize = article.materials.filter((item) =>
        item.sizes.some((size) => !!size.quantity),
      );

      return activeMaterialSize;
    }),
);

export const selectReplenishmentsUploading = createSelector(
  selectReplenishmentState,
  (state) => state.uploading,
);

export const selectReplenishmentsLoaded = createSelector(
  selectReplenishmentState,
  (state) => state.loaded,
);

export const selectReplenishmentsReady = createSelector(
  selectReplenishmentsUploading,
  selectReplenishmentsLoaded,
  (uploading, loaded) => !uploading && loaded,
);

export const selectReplenishmentSizes = createSelector(
  selectReplenishments,
  (articles) =>
    Object.values(articles ?? {}).reduce((acc, article) => {
      const sizesEntries = article.materials
        .flatMap(({ sizes }) => sizes)
        .map((size) => [size.productId, !!size.quantity])
        .filter(([, checked]) => checked);
      return {
        ...acc,
        ...Object.fromEntries(sizesEntries),
      };
    }, {}),
);

export const selectReplenishmentQuantitiesForArticle = memoize(
  (productId: string) =>
    createSelector(selectReplenishments, (replenishments: Articles | null) => {
      const replenishmentData = replenishments?.[productId];
      if (!replenishmentData) return {};
      return Object.fromEntries(
        replenishmentData.materials.map((material) => [
          material.productId,
          material.sizes.reduce((acc: { [key: string]: boolean }, size) => {
            acc[size.productId] = !!size.quantity;
            return acc;
          }, {}),
        ]),
      );
    }),
);

export const selectCmiFiltersConfig = createSelector(
  selectConfigurationValueFrom<
    CmiPageConfigurationKeys.CmiFilters,
    CmiPageConfigurations
  >(
    CMI_PAGE_NAMESPACE,
    CmiPageConfigurationKeys.CmiFilters,
    cmiPageConfigurationsDefaultValues[CmiPageConfigurationKeys.CmiFilters],
  ),
  (filters) => {
    try {
      return JSON.parse(filters as unknown as string);
    } catch (e) {
      return [];
    }
  },
);
